'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject, TouchEvent } from 'react'
import { Eye, Loader2, ZoomIn, ZoomOut } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/lib/i18n/language-provider'
import type { DataPage, PowerPage } from '@/types/synoptique'
import { svgToDataUrl } from '../hooks/report/report-helpers'

interface ViewerPage {
  key: string
  label: string
  dataPage?: DataPage
  powerPage?: PowerPage
}

interface MobileSynopticViewerProps {
  projectName: string
  authorName?: string
  dataPages: DataPage[]
  powerPages: PowerPage[]
  /** Ref partagée avec useExportSvg : le viewer monte un <svg> caché dessus
   *  (createExportSvg* y lit les <defs>, fallback interne sinon). */
  svgHostRef: RefObject<SVGSVGElement | null>
  createExportSvgForDataPage: (page: DataPage, pageNumber: number, totalPages: number) => Promise<SVGSVGElement>
  createExportSvgForPowerPage: (page: PowerPage, pageNumber: number, totalPages: number) => Promise<SVGSVGElement>
}

const MIN_SCALE = 0.5
const MAX_SCALE = 8

export function MobileSynopticViewer({
  projectName,
  authorName,
  dataPages,
  powerPages,
  svgHostRef,
  createExportSvgForDataPage,
  createExportSvgForPowerPage,
}: MobileSynopticViewerProps) {
  const { t } = useLanguage()

  const pages = useMemo<ViewerPage[]>(() => [
    ...dataPages.map((p, i) => ({
      key: `data-${p.id}`,
      label: p.name || t('synoptic.dataPage', { page: i + 1 }),
      dataPage: p,
    })),
    ...powerPages.map((p, i) => ({
      key: `power-${p.id}`,
      label: p.name || t('synoptic.powerPage', { page: i + 1 }),
      powerPage: p,
    })),
  ], [dataPages, powerPages, t])

  const [activeKey, setActiveKey] = useState<string | null>(null)
  const active = pages.find(p => p.key === activeKey) ?? pages[0]

  const [images, setImages] = useState<Record<string, string>>({})
  const [failed, setFailed] = useState<Record<string, boolean>>({})
  const [rendering, setRendering] = useState(false)
  const [scale, setScale] = useState(1)
  const pinchRef = useRef<{ dist: number; scale: number } | null>(null)
  // Anti-doublon : les fonctions createExportSvg* changent d'identité à chaque
  // render du parent — une page n'est générée qu'une fois (succès ou échec).
  const requestedRef = useRef<Set<string>>(new Set())

  // Génération paresseuse du PNG de la page active (même rendu que le PDF)
  useEffect(() => {
    if (!active || images[active.key] || requestedRef.current.has(active.key)) return
    requestedRef.current.add(active.key)
    let cancelled = false
    setRendering(true)

    const pageIndex = pages.indexOf(active)
    const gen = active.dataPage
      ? createExportSvgForDataPage(active.dataPage, pageIndex + 1, pages.length)
      : active.powerPage
        ? createExportSvgForPowerPage(active.powerPage, pageIndex + 1, pages.length)
        : Promise.reject(new Error('no page'))

    gen
      .then(svg => svgToDataUrl(svg))
      .then(url => { if (!cancelled) setImages(prev => ({ ...prev, [active.key]: url })) })
      .catch(() => {
        if (!cancelled) {
          setFailed(prev => ({ ...prev, [active.key]: true }))
          requestedRef.current.delete(active.key) // autorise un retry au prochain passage
        }
      })
      .finally(() => { if (!cancelled) setRendering(false) })

    return () => { cancelled = true }
  }, [active, images, pages, createExportSvgForDataPage, createExportSvgForPowerPage])

  // Pinch-to-zoom : touch-action 'pan-x pan-y' laisse le scroll natif au
  // conteneur, le pinch est géré ici via le ratio de distance des 2 doigts.
  const handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]]
      pinchRef.current = {
        dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        scale,
      }
    }
  }
  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length !== 2 || !pinchRef.current || pinchRef.current.dist === 0) return
    const [a, b] = [e.touches[0], e.touches[1]]
    const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
    setScale(Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchRef.current.scale * (dist / pinchRef.current.dist))))
  }
  const handleTouchEnd = () => { pinchRef.current = null }

  const zoomBy = (delta: number) =>
    setScale(s => Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round((s + delta) * 100) / 100)))

  return (
    <div className="h-screen bg-background text-foreground flex flex-col overflow-hidden">
      {/* Hôte SVG caché — requis par createExportSvg* (clone/fallback des defs) */}
      <svg ref={svgHostRef} className="hidden" aria-hidden="true" />

      {/* En-tête projet */}
      <header className="border-b px-4 py-3 flex items-center gap-3 shrink-0">
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold truncate leading-tight">{projectName}</h1>
          {authorName && (
            <p className="text-xs text-muted-foreground truncate">
              {t('synoptic.share.readOnlyDesc', { author: authorName })}
            </p>
          )}
        </div>
        <Badge variant="secondary" className="shrink-0 gap-1">
          <Eye className="h-3 w-3" />
          {t('synoptic.share.readOnlyBanner')}
        </Badge>
      </header>

      {/* Onglets de pages */}
      {pages.length > 1 && (
        <div className="border-b overflow-x-auto shrink-0">
          <div className="flex gap-1 px-3 py-2 w-max">
            {pages.map(p => (
              <button
                key={p.key}
                onClick={() => { setActiveKey(p.key); setScale(1) }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  p.key === active.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Zone image scrollable + zoomable */}
      <div
        className="flex-1 overflow-auto bg-muted/30"
        style={{ touchAction: 'pan-x pan-y' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {!active ? (
          <div className="h-full flex items-center justify-center p-6 text-center text-muted-foreground">
            {t('synoptic.viewer.noPages')}
          </div>
        ) : images[active.key] ? (
          <img
            src={images[active.key]}
            alt={active.label}
            draggable={false}
            className="block"
            style={{ width: `${scale * 100}%`, maxWidth: 'none' }}
          />
        ) : failed[active.key] ? (
          <div className="h-full flex items-center justify-center p-6 text-center text-muted-foreground">
            {t('synoptic.viewer.renderError')}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
      </div>

      {/* Contrôles de zoom */}
      <div className="border-t px-4 py-2 flex items-center justify-center gap-2 shrink-0">
        <Button variant="outline" size="icon" onClick={() => zoomBy(-0.25)} aria-label="Zoom out">
          <ZoomOut className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="min-w-[64px] tabular-nums"
          onClick={() => setScale(1)}
        >
          {rendering ? '…' : `${Math.round(scale * 100)}%`}
        </Button>
        <Button variant="outline" size="icon" onClick={() => zoomBy(0.25)} aria-label="Zoom in">
          <ZoomIn className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
